import { computed, isRef, reactive, ref, watch, type Ref } from 'vue'
import useResize from '@/hooks/useResize'

type FloatBubbleConfig = {
  id: string
  stackSize: number | Ref<number>
  visible: boolean | Ref<boolean>
}

type FloatBubbleItem = {
  id: string
  stackSize: number
  offsetY: number
  visible: boolean
}

const SAFE_TOP = 50
const SAFE_BOTTOM = 85

const state = reactive({
  registry: new Map<string, FloatBubbleItem>(),
  order: [] as string[]
})

export function useFloatStack() {
  const clampY = (value: number) => {
    const maxY = window.innerHeight - SAFE_BOTTOM
    const safeMax = Math.max(maxY, SAFE_TOP)
    return Math.min(Math.max(value, SAFE_TOP), safeMax)
  }

  const reStackAll = () => {
    let currentOffset = 0
    for (const id of state.order) {
      const item = state.registry.get(id)
      if (!item || !item.visible) continue

      const nextOffset = currentOffset + item.stackSize
      const baseY = window.innerHeight - SAFE_BOTTOM - nextOffset
      item.offsetY = clampY(baseY)
      currentOffset = nextOffset
    }
  }

  const insertFloatBubble = (config: FloatBubbleConfig) => {
    const visibleRef = isRef(config.visible) ? config.visible : ref(config.visible)
    const stackSizeRef = isRef(config.stackSize) ? config.stackSize : ref(config.stackSize)

    if (!state.registry.has(config.id)) {
      state.registry.set(config.id, {
        id: config.id,
        stackSize: stackSizeRef.value,
        offsetY: 0,
        visible: visibleRef.value
      })
      state.order.push(config.id)
    } else {
      state.registry.get(config.id)!.stackSize = stackSizeRef.value
    }

    const item = state.registry.get(config.id)!
    watch(
      stackSizeRef,
      (stackSize) => {
        item.stackSize = stackSize
        if (item.visible) reStackAll()
      },
      { immediate: true }
    )
    watch(
      visibleRef,
      (isVisible) => {
        item.visible = isVisible
        if (isVisible) reStackAll()
      },
      { immediate: true }
    )

    return computed({
      get: () => state.registry.get(config.id)?.offsetY ?? 0,
      set: (nextY) => {
        const target = state.registry.get(config.id)
        if (target) target.offsetY = nextY
      }
    })
  }

  useResize(reStackAll)

  return {
    insertFloatBubble
  }
}
